import { v4 } from 'uuid';
import { BANNER_PADRAO, VIDEO_PADRAO, MAPA_PADRAO, FORMULARIO_PADRAO } from '@/utils/constantesComponentes';
import { CAMPOS_DISPONIVEIS, OUTROS_CAMPOS } from '@/constants/componentesCatalogo';

const criarGradePadrao = (linhas = 2, colunas = 2) => {
    // Criar células como children (igual o formulário faz)
    const celulas = [];
    for (let i = 0; i < linhas; i++) {
        for (let j = 0; j < colunas; j++) {
            celulas.push({
                componenteId: v4(),
                componente: 'celulaGrade',
                props: {
                    row: i,
                    col: j,
                },
                children: [],
            });
        }
    }

    return {
        componenteId: v4(),
        componente: 'grade',
        props: {
            linhas,
            colunas,
            alignItems: 'start',
            justifyItems: 'start',
            gap: '12px',
        },
        children: celulas,
    };
};

const catalogoCampos = [...CAMPOS_DISPONIVEIS, ...OUTROS_CAMPOS].reduce((acc, campo) => {
    acc[campo.componente] = campo;
    return acc;
}, {});

const criarFormularioPadrao = () => {
    return {
        componenteId: v4(),
        componente: 'formulario',
        props: {
            design: {
                imagemFundo: '',
                corFundo: '#ffffff',
                tipografia: 'Arial',
                gap: '16px',
                padding: '24px',
                tamanhoFonte: '16px',
            },
        },
        children: [
            {
                componenteId: v4(),
                componente: 'cpf',
                props: {
                    label: 'CPF',
                    name: 'cpf',
                    required: true,
                },
                children: [],
            },
            {
                componenteId: v4(),
                componente: 'nome',
                props: {
                    label: 'Nome',
                    name: 'nome',
                    required: true,
                    showIcon: true,
                },
                children: [],
            },
            {
                componenteId: v4(),
                componente: 'dataNascimento',
                props: {
                    label: 'Data de Nascimento',
                    name: 'dataNascimento',
                    required: true,
                    showIcon: true,
                },
                children: [],
            },
            {
                componenteId: v4(),
                componente: 'genero',
                props: {
                    label: 'Gênero',
                    name: 'genero',
                    required: true,
                    opcoesList: [
                        { label: 'Mulher', value: 'mulher' },
                        { label: 'Homem', value: 'homem' },
                        { label: 'Outros', value: 'outros' },
                    ],
                    showIcon: true,
                },
                children: [],
            },
            {
                componenteId: v4(),
                componente: 'botaoEnviar',
                props: {
                    texto: 'Inscreva-se',
                    alinhamento: 'end',
                    cor: '#0e88b3',
                    tamanho: 'medio',
                    estilo: 'primario',
                },
                children: [],
            },
        ],
    };
};

export const ComponenteFactory = {
    /**
     * Cria um novo componente com UUID único
     * @param {string} tipo - Tipo do componente
     * @param {Object} props - Propriedades do componente (opcional)
     * @param {Array} children - Componentes filhos (opcional)
     * @returns {Object} Novo componente
     */
    criar(tipo, props = {}, children = []) {
        if (tipo === 'formulario') {
            return criarFormularioPadrao();
        }

        if (tipo === 'grade') {
            return criarGradePadrao(props.linhas, props.colunas);
        }

        const propsPadrao = this.obterPropsPadrao(tipo);
        return {
            componenteId: v4(),
            componente: tipo,
            props: { ...propsPadrao, ...props },
            children: Array.isArray(children) ? children.map((child) => this.criar(child.componente, child.props, child.children)) : [],
        };
    },

    /**
     * Clona um componente existente com novo UUID
     * @param {Object} componente - Componente a ser clonado
     * @returns {Object} Componente clonado com novo UUID
     */
    clonar(componente) {
        if (!componente) return null;
        return this.criar(componente.componente, JSON.parse(JSON.stringify(componente.props || {})), componente.children || []);
    },

    /**
     * Obtém propriedades padrão para um tipo de componente
     * @param {string} tipo - Tipo do componente
     * @returns {Object} Propriedades padrão
     */
    obterPropsPadrao(tipo) {
        const config = catalogoCampos[tipo] || {};
        const propsBase = {
            label: config.titulo || tipo,
            name: tipo,
        };

        switch (tipo) {
            // Componentes
            case 'card':
                return {
                    backgroundColor: '#ffffff',
                    titulo: 'Edite para adicionar um título',
                    descricao: 'Edite para adicionar um texto de descrição',
                    borderRadius: '4px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                    flexDirection: 'column',
                };
            case 'video':
                return { ...VIDEO_PADRAO };
            case 'mapa':
                return { ...MAPA_PADRAO };
            case 'formulario':
                return { ...FORMULARIO_PADRAO };
            case 'texto':
                return {
                    fontSize: '16px',
                };
            case 'banner':
                return { ...BANNER_PADRAO };
            case 'botao':
                return {
                    texto: 'Clique aqui',
                    cor: '#007bff',
                    link: '',
                    tamanho: 'medio',
                    estilo: 'primario',
                    backgroundColor: '#1097C6',
                };
            case 'imagem':
                return {};

            // Layouts
            case 'grade':
                return {
                    linhas: 1,
                    colunas: 2,
                };
            case 'container':
                return {
                    style: {
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'start',
                        alignItems: 'start',
                    },
                };

            // Formulario
            case 'botaoEnviar':
                return {
                    ...propsBase,
                    cor: '#0e88b3',
                    tamanho: 'medio',
                    estilo: 'primario',
                };

            case 'cargo':
                return { ...propsBase, length: 100, showIcon: true };
            case 'empresa':
                return { ...propsBase, length: 250, showIcon: true };
            case 'setor':
                return { ...propsBase, length: 100, showIcon: true };
            case 'campoDropdown':
                return {
                    ...propsBase,
                    opcoesList: [
                        { label: 'Sim', value: 'sim' },
                        { label: 'Não', value: 'nao' },
                    ],
                    showIcon: true,
                };
            // Campos que só precisam das props base (label e name dinâmicos)
            case 'email':
            case 'telefone':
            case 'campoTexto':
            case 'campoNumerico':
            case 'campoCNPJ':
            case 'campoCep':
            case 'campoEmail':
            case 'campoCheckbox':
            case 'campoData':
                return { ...propsBase, showIcon: true };

            default:
                return {};
        }
    },
};
